import React, { useContext, useEffect, useState, useRef } from 'react'
import { Helmet } from 'react-helmet'
import { AuthContext } from '../../context/auth'
import apinode from '../../services/apinode'
import { useNavigate } from 'react-router-dom'
import ReCAPTCHA from 'react-google-recaptcha'
import { toast } from 'react-toastify'
import styled, { keyframes } from 'styled-components'

// ─── Animations ───────────────────────────────────────────────────────────────
const spin = keyframes`to { transform: rotate(360deg); }`
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`

// ─── Layout ───────────────────────────────────────────────────────────────────
export const Container = styled.div`
  display: flex;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  background: #f0f2f5;
  padding: 2rem;
  font-family: 'DM Sans', sans-serif;

  @media (max-width: 640px) {
    padding: 1rem;
    align-items: flex-start;
    padding-top: 2rem;
  }
`

export const Card = styled.div`
  display: flex;
  width: 100%;
  max-width: 860px;
  min-height: 540px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
  animation: ${fadeIn} 0.4s ease;

  @media (max-width: 640px) {
    flex-direction: column;
    min-height: unset;
    border-radius: 14px;
    max-width: 100%;
  }
`

// ─── Left Panel ───────────────────────────────────────────────────────────────
export const LeftPanel = styled.div`
  flex: 0 0 300px;
  background: #0a1628;
  padding: 3rem 2rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -80px; left: -80px;
    width: 280px; height: 280px;
    border-radius: 50%;
    background: rgba(55, 138, 221, 0.12);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -40px; right: -60px;
    width: 200px; height: 200px;
    border-radius: 50%;
    background: rgba(29, 158, 117, 0.10);
  }

  @media (max-width: 640px) {
    flex: none;
    padding: 1.25rem 1.25rem 1rem;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    &::before, &::after { display: none; }
  }
`

export const BrandLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  z-index: 1;
`

export const BrandIcon = styled.div`
  width: 38px; height: 38px;
  background: #378add;
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px;
  color: #fff;

  @media (max-width: 640px) {
    width: 32px; height: 32px;
    font-size: 17px;
    border-radius: 8px;
  }
`

export const BrandName = styled.span`
  font-family: 'DM Serif Display', serif;
  font-size: 18px;
  color: #fff;

  @media (max-width: 640px) {
    font-size: 16px;
  }
`

export const LeftHeadline = styled.div`
  position: relative;
  z-index: 1;

  h2 {
    font-family: 'DM Serif Display', serif;
    font-size: 26px;
    font-weight: 400;
    color: #fff;
    line-height: 1.3;
    margin-bottom: 12px;

    em {
      font-style: italic;
      color: #5dcaa5;
    }
  }

  p {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.45);
    line-height: 1.6;
  }

  @media (max-width: 640px) {
    display: none;
  }
`

export const LeftDots = styled.div`
  display: flex;
  gap: 6px;
  position: relative;
  z-index: 1;

  span {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);

    &.active {
      width: 18px;
      border-radius: 3px;
      background: #378add;
    }
  }

  @media (max-width: 640px) {
    display: none;
  }
`

// Badge exibido no mobile no lugar do headline/dots
export const MobileBadge = styled.div`
  display: none;

  @media (max-width: 640px) {
    display: block;
    font-size: 11px;
    color: rgba(255,255,255,0.45);
    letter-spacing: 0.4px;
  }
`

// ─── Right Panel (Form) ───────────────────────────────────────────────────────
export const RightPanel = styled.div`
  flex: 1;
  padding: 2.5rem 2.5rem;
  background: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 640px) {
    padding: 1.5rem 1.25rem 2rem;
  }
`

export const FormHeader = styled.div`
  margin-bottom: 1.75rem;

  h1 {
    font-family: 'DM Serif Display', serif;
    font-size: 24px;
    font-weight: 400;
    color: #111;
    margin-bottom: 4px;

    @media (max-width: 640px) {
      font-size: 20px;
    }
  }

  p {
    font-size: 13px;
    color: #888;
  }
`

export const FieldGroup = styled.div`
  margin-bottom: 14px;
`

export const FieldLabel = styled.label`
  display: block;
  font-size: 11px;
  font-weight: 500;
  color: #888;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 6px;
`

export const FieldWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  .field-icon {
    position: absolute;
    left: 12px;
    font-size: 16px;
    color: #bbb;
    pointer-events: none;
    line-height: 1;
  }

  input {
    width: 100%;
    height: 44px;
    padding: 0 12px 0 38px;
    border: 1px solid #e0e0e0;
    border-radius: 9px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    color: #111;
    background: #fff;
    transition: border-color 0.15s, box-shadow 0.15s;
    -webkit-appearance: none;

    &::placeholder { color: #bbb; }

    &:focus {
      outline: none;
      border-color: #378add;
      box-shadow: 0 0 0 3px rgba(55, 138, 221, 0.12);
    }
  }
`

export const TogglePassword = styled.button`
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #bbb;
  font-size: 16px;
  line-height: 1;
  display: flex;
  align-items: center;

  &:hover { color: #888; }
`

export const CnpjStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 11px;
  border-radius: 8px;
  font-size: 12.5px;
  margin-top: 7px;

  ${({ $type }) => $type === 'success' && `
    background: #eaf3de; color: #3b6d11; border: 1px solid #97c459;
  `}
  ${({ $type }) => $type === 'error' && `
    background: #fcebeb; color: #a32d2d; border: 1px solid #f09595;
  `}
  ${({ $type }) => $type === 'loading' && `
    background: #f5f5f5; color: #888;
  `}
`

export const Spinner = styled.div`
  width: 12px; height: 12px;
  border: 2px solid rgba(0,0,0,0.1);
  border-top-color: #378add;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  flex-shrink: 0;
`

export const BtnSpinner = styled.div`
  width: 16px; height: 16px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`

export const LoginButton = styled.button`
  width: 100%;
  height: 46px;
  background: ${({ disabled }) => (disabled ? '#e0e0e0' : '#0a1628')};
  color: ${({ disabled }) => (disabled ? '#aaa' : '#fff')};
  border: none;
  border-radius: 9px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14.5px;
  font-weight: 500;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s, transform 0.1s;
  margin-bottom: 14px;
  -webkit-tap-highlight-color: transparent;

  &:hover:not(:disabled) { background: #142036; }
  &:active:not(:disabled) { transform: scale(0.99); }
`

export const IpFooter = styled.p`
  font-size: 12px;
  color: #bbb;
  display: flex;
  align-items: center;
  gap: 6px;
`

// ─── Component ────────────────────────────────────────────────────────────────
const LAST_CNPJ_KEY = 'lastCnpj'

function Login() {
  const [usuario, setUsuario] = useState('')
  const [senha, setSenha] = useState('')
  const [showSenha, setShowSenha] = useState(false)
  const [sucesso, setSucesso] = useState('')
  const { setLogado } = useContext(AuthContext)
  const [cnpjempresa, setCnpjEmpresa] = useState('')
  const [cnpj, setCnpj] = useState(() => localStorage.getItem(LAST_CNPJ_KEY) || '')
  const [isRequestLoading, setIsRequestLoading] = useState(false)
  const [isRequestLoginLoading, setIsRequestLoginLoading] = useState(false)
  const [captchaValido, setCaptchaValido] = useState(false)
  const [ip, setIp] = useState('')
  const navigate = useNavigate()

  // Busca IP e, se houver CNPJ salvo, já verifica a empresa automaticamente
  useEffect(() => {
    async function init() {
      try {
        const res = await fetch('https://api.ipify.org?format=json')
        const data = await res.json()
        setIp(data.ip)
      } catch {}

      const saved = localStorage.getItem(LAST_CNPJ_KEY)
      if (saved && saved.replace(/\D/g, '').length === 14) {
        await verifyCompany(saved)
      }
    }
    init()
  }, [])

  function formatCNPJ(value) {
    let v = value.replace(/\D/g, '').slice(0, 14)
    if (v.length > 12)     v = v.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, '$1.$2.$3/$4-$5')
    else if (v.length > 8) v = v.replace(/^(\d{2})(\d{3})(\d{3})(\d{0,4})/, '$1.$2.$3/$4')
    else if (v.length > 5) v = v.replace(/^(\d{2})(\d{3})(\d{0,3})/, '$1.$2.$3')
    else if (v.length > 2) v = v.replace(/^(\d{2})(\d{0,3})/, '$1.$2')
    return v
  }

  // Função central de verificação – aceita valor explícito ou usa o state
  async function verifyCompany(value) {
    const raw = (value || cnpj).replace(/\D/g, '')
    if (raw.length !== 14) return

    setIsRequestLoading(true)
    try {
      const response = await apinode.get('/auth/cnpj', { params: { cnpj: value || cnpj } })
      localStorage.setItem('idconta',   response.data.idconta)
      localStorage.setItem('nomeconta', response.data.razaosocial)
      localStorage.setItem('usa_wms',   response.data.usa_wms.trim())
      localStorage.setItem(LAST_CNPJ_KEY, value || cnpj) // salva para próxima vez
      setCnpjEmpresa(response.data.razaosocial)
      setSucesso('')
    } catch {
      setCnpjEmpresa('')
      setSucesso('N')
    } finally {
      setIsRequestLoading(false)
    }
  }

  // Chamado pelo onBlur do campo
  function userCompanyVerification() {
    verifyCompany(cnpj)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!captchaValido) {
      toast.warning('Confirme o CAPTCHA!')
      return
    }
    try {
      setIsRequestLoginLoading(true)
      const response = await apinode.get(`/auth/login/${localStorage.idconta}`, {
        params: { usuario, senha, conta: localStorage.idconta },
      })
      localStorage.setItem('logado',    'S')
      localStorage.setItem('idusuario', response.data.id_usuario)
      localStorage.setItem('username',  response.data.nome)
      localStorage.setItem('admin',     response.data.admin)
      localStorage.setItem('cnpj',      cnpj)
      localStorage.setItem('access',    btoa(senha))
      setLogado(true)
      setSucesso('S')
      navigate('/')
    } catch {
      setLogado(false)
      localStorage.setItem('logado', 'N')
      setSucesso('N')
      toast.error('Erro ao fazer login!')
    } finally {
      setIsRequestLoginLoading(false)
    }
  }

  const cnpjStatus = () => {
    if (isRequestLoading)
      return (
        <CnpjStatus $type="loading">
          <Spinner /> Verificando empresa...
        </CnpjStatus>
      )
    if (cnpjempresa)
      return (
        <CnpjStatus $type="success">
          ✓ {cnpjempresa}
        </CnpjStatus>
      )
    if (sucesso === 'N')
      return (
        <CnpjStatus $type="error">
          ✕ CNPJ não informado ou inexistente
        </CnpjStatus>
      )
    return null
  }

  const isDisabled = !usuario || !senha || !cnpjempresa || !captchaValido

  return (
    <Container>
      <Helmet>
        <title>Login</title>
        <link
          href="https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500&display=swap"
          rel="stylesheet"
        />
        {/* Tabler Icons para os ícones de campo */}
        <link
          href="https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/dist/tabler-icons.min.css"
          rel="stylesheet"
        />
      </Helmet>

      <Card>
        {/* ── Painel esquerdo ── */}
        <LeftPanel>
          <BrandLogo>
            <BrandIcon>⚡</BrandIcon>
            <BrandName>Ziphub</BrandName>
          </BrandLogo>

          {/* Subtítulo visível só no mobile (barra superior) */}
          <MobileBadge>Gestão de canais de venda</MobileBadge>

          <LeftHeadline>
            <h2>
              Gerencie seus <em>canais</em> com inteligência.
            </h2>
            <p>
              Integração completa com os principais marketplaces do Brasil em uma única plataforma.
            </p>
          </LeftHeadline>

          <LeftDots>
            <span className="active" />
            <span />
            <span />
          </LeftDots>
        </LeftPanel>

        {/* ── Painel direito / formulário ── */}
        <RightPanel>
          <form onSubmit={handleSubmit}>
            <FormHeader>
              <h1>Bem-vindo de volta</h1>
              <p>Acesse sua conta para continuar</p>
            </FormHeader>

            {/* CNPJ */}
            <FieldGroup>
              <FieldLabel htmlFor="cnpj">CNPJ da empresa</FieldLabel>
              <FieldWrap>
                <i className="ti ti-building field-icon" aria-hidden="true" />
                <input
                  id="cnpj"
                  type="tel"
                  inputMode="numeric"
                  placeholder="00.000.000/0000-00"
                  value={cnpj}
                  onChange={(e) => {
                    const formatted = formatCNPJ(e.target.value)
                    setCnpj(formatted)
                    // Limpa status anterior se o usuário editar
                    if (cnpjempresa) setCnpjEmpresa('')
                    if (sucesso === 'N') setSucesso('')
                  }}
                  onBlur={userCompanyVerification}
                  maxLength={18}
                />
              </FieldWrap>
              {cnpjStatus()}
            </FieldGroup>

            {/* Usuário */}
            <FieldGroup>
              <FieldLabel htmlFor="usuario">Usuário</FieldLabel>
              <FieldWrap>
                <i className="ti ti-user field-icon" aria-hidden="true" />
                <input
                  id="usuario"
                  type="text"
                  placeholder="Nome de usuário"
                  value={usuario}
                  onChange={(e) => setUsuario(e.target.value)}
                  autoCapitalize="none"
                  autoCorrect="off"
                />
              </FieldWrap>
            </FieldGroup>

            {/* Senha */}
            <FieldGroup style={{ marginBottom: 20 }}>
              <FieldLabel htmlFor="senha">Senha</FieldLabel>
              <FieldWrap>
                <i className="ti ti-lock field-icon" aria-hidden="true" />
                <input
                  id="senha"
                  type={showSenha ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(e) }}
                  style={{ paddingRight: 38 }}
                />
                <TogglePassword
                  type="button"
                  onClick={() => setShowSenha((v) => !v)}
                  aria-label={showSenha ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  <i
                    className={showSenha ? 'ti ti-eye-off' : 'ti ti-eye'}
                    aria-hidden="true"
                  />
                </TogglePassword>
              </FieldWrap>
            </FieldGroup>

            {/* ReCAPTCHA */}
            <div style={{ marginBottom: 20 }}>
              <ReCAPTCHA
                sitekey="6Lep2VorAAAAAKs25T7S78rGLlDJYv3ZmyY3nt4M"
                onChange={async (token) => {
                  try {
                    const response = await apinode.post('/verify-captcha', { token })
                    setCaptchaValido(!!response.data.success)
                  } catch {
                    setCaptchaValido(false)
                  }
                }}
              />
            </div>

            <LoginButton type="submit" disabled={isDisabled || isRequestLoginLoading}>
              {isRequestLoginLoading ? (
                <><BtnSpinner /> Entrando...</>
              ) : (
                'Acessar →'
              )}
            </LoginButton>

            <IpFooter>🌐 Seu IP: {ip || '...'}</IpFooter>
          </form>
        </RightPanel>
      </Card>
    </Container>
  )
}

export default Login